/**
 * Pure helpers for the workspace's responsive single-pane (mobile) layout.
 *
 * Below {@link MOBILE_BREAKPOINT} the IDE can't show chat + preview/editor as a
 * side-by-side row (each pane ends up ~130–240px wide). Instead it collapses to
 * ONE full-width surface at a time, switched by a bottom segmented nav
 * (Chat | Preview | Code). These helpers map between that nav and the panel's
 * `mobilePane` + `mainView` state. Kept framework-free so they're unit-tested
 * without a DOM (the `matchMedia` React hook lives in `Workspace.tsx`).
 *
 * @module
 */

/** Width (px) below which the IDE collapses to the single-pane mobile layout. */
export const MOBILE_BREAKPOINT = 768

/**
 * Whether a viewport width should use the mobile single-pane layout.
 * @param width - The viewport width in CSS px.
 * @returns True for phone widths (`< 768`); tablets/desktop keep the 2-pane row.
 */
export function isMobileWidth(width: number): boolean {
  return width < MOBILE_BREAKPOINT
}

/** Which surface the mobile single-pane layout is currently showing. */
export type MobilePane = 'chat' | 'main'

/** The main pane's content — shared with the desktop Preview/Code toggle. */
export type MainView = 'preview' | 'editor'

/** A mobile bottom-nav target. */
export type MobileNavTarget = 'chat' | 'preview' | 'code'

/** The ordered set of mobile bottom-nav targets. */
export const MOBILE_NAV_TARGETS: readonly MobileNavTarget[] = ['chat', 'preview', 'code']

/**
 * Resolves a mobile bottom-nav tap into the pane (and, for the main pane, the
 * view) to show. `chat` shows the chat pane; `preview`/`code` show the main
 * pane with the matching {@link MainView}.
 * @param target - The tapped nav target.
 * @returns The pane to show and, for the main pane, which view.
 */
export function resolveMobileNav(target: MobileNavTarget): { pane: MobilePane; view?: MainView } {
  switch (target) {
    case 'chat':
      return { pane: 'chat' }
    case 'preview':
      return { pane: 'main', view: 'preview' }
    case 'code':
      return { pane: 'main', view: 'editor' }
  }
}

/**
 * The active mobile-nav target for a given pane + view — drives the nav's
 * selected (highlighted) state. The chat pane is always `chat`; the main pane
 * is `code` when editing, else `preview`.
 * @param pane - The currently-shown pane.
 * @param view - The main pane's current view.
 * @returns The nav target to render as selected.
 */
export function activeMobileNav(pane: MobilePane, view: MainView): MobileNavTarget {
  if (pane === 'chat') return 'chat'
  return view === 'editor' ? 'code' : 'preview'
}
